import { api } from '@/api/client'

export const INTAKE_API = '/scientific/template-intakes'
export interface IntakeSummary {
  template_id: string
  title: string
  owner_id: string
  owner_email: string
  owner_name?: string
  programs?: string[]
  // 'needs_developer_attention' is the pre-queue status; rows keep it until the backfill ran.
  status: 'draft' | 'queued' | 'in_research' | 'open' | 'claimed' | 'returned' | 'approved' | 'needs_developer_attention'
  revision: number
  /** The researcher job's own blocked state, recorded by the one-minute sweep (intake.summary). */
  research_blocked?: boolean
  updated_at: string
  submitted_at?: string
  return_note?: string
}
export interface IntakeFile {
  file_id: string
  filename: string
  content_type: string
  size: number
  status: 'pending' | 'uploaded'
}
export interface IntakeText { title: string; explanation: string; partial: string }
export interface IntakeDetail extends IntakeText {
  template: IntakeSummary
  files: IntakeFile[]
  submissions: { submission_id: string; submitted_at: string }[]
  can_edit: boolean
  can_edit_source?: boolean
  source_changed?: boolean
  speech_enabled: boolean
  max_files: number
  max_file_bytes: number
  max_text_bytes: number
}
export const SUBMITTED_STATUSES: IntakeSummary['status'][] = ['queued', 'in_research', 'open', 'claimed', 'needs_developer_attention']
export const isSubmitted = (status: IntakeSummary['status']) => SUBMITTED_STATUSES.includes(status)
export const intakeStatus = (status: IntakeSummary['status']) => ({
  draft: 'Draft', queued: 'Queued for the researcher agent', in_research: 'Research in progress',
  open: 'Proposals ready for review', claimed: 'In expert review', returned: 'Returned for edits',
  approved: 'Proposals approved', needs_developer_attention: 'Research and review',
})[status]
export const intakeTodo = (status: IntakeSummary['status'], returnNote?: string) => ({
  draft: 'Upload your data and describe the experiments.',
  queued: 'Queued for the researcher agent — review its ideas shortly.',
  in_research: 'The researcher agent is working on proposals.',
  open: 'Proposals are ready. Claim the review.',
  claimed: 'Open the proposal review and decide.',
  returned: returnNote ?? 'Returned for edits.',
  approved: 'Workflows approved. Open the template to follow each one through its stages.',
  needs_developer_attention: 'Open research progress and proposal review.',
})[status]

export async function intakeDownload(id: string, file?: string, submission?: string) {
  const { url } = await api.get<{ url: string }>(`${INTAKE_API}/${id}/download`, { file, submission })
  window.location.assign(url)
}

// Text recovery is local to this user/template. No audio or provider tokens are stored.
type Recovery = IntakeText & { revision: number }
function recoveryStore<T>(key: string, operation: 'get' | 'put' | 'delete', value?: Recovery): Promise<T> {
  return new Promise((resolve, reject) => {
    const opening = indexedDB.open('seldon-template-drafts', 1)
    opening.onupgradeneeded = () => opening.result.createObjectStore('drafts')
    opening.onerror = () => reject(opening.error)
    opening.onblocked = () => reject(new Error('Local recovery is unavailable.'))
    opening.onsuccess = () => {
      const db = opening.result
      try {
        const transaction = db.transaction('drafts', operation === 'get' ? 'readonly' : 'readwrite')
        const store = transaction.objectStore('drafts')
        const request = operation === 'get' ? store.get(key) : operation === 'put' ? store.put(value, key) : store.delete(key)
        transaction.oncomplete = () => { db.close(); resolve(request.result as T) }
        transaction.onabort = transaction.onerror = () => { db.close(); reject(transaction.error ?? new Error('Local recovery is unavailable.')) }
      } catch (error) { db.close(); reject(error) }
    }
  })
}
export const recovery = {
  get: (key: string) => recoveryStore<Recovery | undefined>(key, 'get'),
  put: (key: string, value: Recovery) => recoveryStore<void>(key, 'put', value),
  remove: (key: string) => recoveryStore<void>(key, 'delete'),
}
